import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://hydro-protect-7.emergent.host";

const SEO_BY_LANG = {
  de: {
    title: "HannoNexus — Integrated Infrastructure Ecosystem",
    description:
      "Energie · Mobilität · Infrastruktur · Digitale Systeme. Integrierte Ökosysteme für die portugiesischsprachigen Länder. Powered by BSGOTECH.",
    locale: "de_DE",
  },
  en: {
    title: "HannoNexus — Integrated Infrastructure Ecosystem",
    description:
      "Energy · Mobility · Infrastructure · Digital Systems. Integrated ecosystems for the Portuguese-speaking countries. Powered by BSGOTECH.",
    locale: "en_GB",
  },
  fr: {
    title: "HannoNexus — Écosystème d'Infrastructure Intégré",
    description:
      "Énergie · Mobilité · Infrastructure · Systèmes Numériques. Écosystèmes intégrés pour les pays lusophones. Powered by BSGOTECH.",
    locale: "fr_FR",
  },
  pt: {
    title: "HannoNexus — Ecossistema de Infraestrutura Integrado",
    description:
      "Energia · Mobilidade · Infraestrutura · Sistemas Digitais. Ecossistemas integrados para os países lusófonos. Powered by BSGOTECH.",
    locale: "pt_PT",
  },
};

const PRODUCT_TITLE_PREFIX = {
  de: "Produkt",
  en: "Product",
  fr: "Produit",
  pt: "Produto",
};

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, hreflang, href) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export const SEOHead = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const lang = (i18n.language || "de").split("-")[0];
  const seo = SEO_BY_LANG[lang] || SEO_BY_LANG.de;

  React.useEffect(() => {
    // Build current path without lang prefix
    const parts = location.pathname.split("/").filter(Boolean);
    const pathSegments = ["en", "de", "fr", "pt"].includes(parts[0])
      ? parts.slice(1)
      : parts;
    const cleanPath = pathSegments.length ? "/" + pathSegments.join("/") : "";

    // Extract product slug if on a product page
    const productSlug =
      pathSegments[0] === "products" && pathSegments[1] ? pathSegments[1] : null;

    // Title: product page gets product-specific title
    let title = seo.title;
    let description = seo.description;
    if (productSlug) {
      const productName = t(`products.${productSlug}.name`, { defaultValue: `HX ${productSlug}` });
      const productTagline = t(`products.${productSlug}.tagline`, { defaultValue: "" });
      title = `${productName} — ${PRODUCT_TITLE_PREFIX[lang]} HX Ecosystems`;
      if (productTagline) description = `${productName}. ${productTagline}`;
    }

    // Title + description
    document.title = title;
    upsertMeta("name", "description", description);

    // Open Graph
    const fullUrl = `${SITE_URL}/${lang}${cleanPath}`;
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", fullUrl);
    upsertMeta("property", "og:locale", seo.locale);

    // Twitter
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    // Canonical for current variant
    upsertLink("canonical", null, fullUrl);

    // Hreflang: refresh all
    Object.keys(SEO_BY_LANG).forEach((code) => {
      upsertLink("alternate", code, `${SITE_URL}/${code}${cleanPath}`);
    });
    upsertLink("alternate", "x-default", `${SITE_URL}${cleanPath || "/"}`);

    // <html lang>
    document.documentElement.lang = lang;
  }, [lang, location.pathname, seo, t]);

  return null;
};
